/** Time helpers. Durations never use seconds, so the UI cannot show alert latency. */

const TORONTO = "America/Toronto";

export const ONTARIO_HOLIDAYS = new Set<string>([
  "2025-01-01",
  "2025-02-17",
  "2025-04-18",
  "2025-05-19",
  "2025-07-01",
  "2025-08-04",
  "2025-09-01",
  "2025-10-13",
  "2025-12-25",
  "2025-12-26",
  "2026-01-01",
  "2026-02-16",
  "2026-04-03",
  "2026-05-18",
  "2026-07-01",
  "2026-08-03",
  "2026-09-07",
  "2026-10-12",
  "2026-12-25",
  "2026-12-26",
  "2027-01-01",
  "2027-02-15",
  "2027-03-26",
  "2027-05-24",
  "2027-07-01",
  "2027-08-02",
  "2027-09-06",
  "2027-10-11",
  "2027-12-25",
  "2027-12-27"
]);

export interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  ymd: string;
  weekday: number;
}

export function zonedParts(date: Date, timeZone = TORONTO): ZonedParts {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    weekday: "short"
  });
  const bag: Record<string, string> = {};
  fmt.formatToParts(date).forEach((part) => {
    bag[part.type] = part.value;
  });
  const year = Number(bag.year);
  const month = Number(bag.month);
  const day = Number(bag.day);
  const hour = Number(bag.hour);
  const minute = Number(bag.minute);
  const ymd = `${bag.year}-${bag.month}-${bag.day}`;
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return { year, month, day, hour, minute, ymd, weekday };
}

export function isOntarioHoliday(ymd: string): boolean {
  return ONTARIO_HOLIDAYS.has(ymd);
}

/** Mon–Fri 08:30–16:30 America/Toronto, excluding the statutory holidays above. */
export function isOntarioBusinessHours(date: Date): boolean {
  const parts = zonedParts(date);
  if (parts.weekday === 0 || parts.weekday === 6) return false;
  if (isOntarioHoliday(parts.ymd)) return false;
  const minutes = parts.hour * 60 + parts.minute;
  return minutes >= 8 * 60 + 30 && minutes < 16 * 60 + 30;
}

function addCalendarDays(
  year: number,
  month: number,
  day: number,
  days: number
): { year: number; month: number; day: number } {
  const utc = new Date(Date.UTC(year, month - 1, day));
  utc.setUTCDate(utc.getUTCDate() + days);
  return { year: utc.getUTCFullYear(), month: utc.getUTCMonth() + 1, day: utc.getUTCDate() };
}

function isBusinessYmd(year: number, month: number, day: number): boolean {
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  if (weekday === 0 || weekday === 6) return false;
  const ymd = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  return !isOntarioHoliday(ymd);
}

/** Next Ontario business morning (08:30) after `date`, as a readable local label. */
export function nextOntarioBusinessMorning(date: Date): string {
  const parts = zonedParts(date);
  let cursor = { year: parts.year, month: parts.month, day: parts.day };
  const during = isOntarioBusinessHours(date);
  if (!during) {
    const minutes = parts.hour * 60 + parts.minute;
    const stillToday = isBusinessYmd(parts.year, parts.month, parts.day) && minutes < 8 * 60 + 30;
    if (!stillToday) {
      cursor = addCalendarDays(cursor.year, cursor.month, cursor.day, 1);
    }
  } else {
    return formatYmdTime(parts.year, parts.month, parts.day, 8, 30);
  }
  for (let i = 0; i < 14; i += 1) {
    if (isBusinessYmd(cursor.year, cursor.month, cursor.day)) {
      return formatYmdTime(cursor.year, cursor.month, cursor.day, 8, 30);
    }
    cursor = addCalendarDays(cursor.year, cursor.month, cursor.day, 1);
  }
  return formatYmdTime(cursor.year, cursor.month, cursor.day, 8, 30);
}

function formatYmdTime(year: number, month: number, day: number, hour: number, minute: number): string {
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour + 4, minute));
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TORONTO,
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(utcGuess);
}

export function addHours(iso: string, hours: number): string {
  return new Date(new Date(iso).getTime() + hours * 3600000).toISOString();
}

export function addDays(iso: string, days: number): string {
  return new Date(new Date(iso).getTime() + days * 86400000).toISOString();
}

/** UTC instant whose Toronto wall clock is the given civil time. */
export function torontoTime(year: number, month: number, day: number, hour: number, minute: number): Date {
  let utc = Date.UTC(year, month - 1, day, hour + 5, minute);
  for (let i = 0; i < 4; i += 1) {
    const parts = zonedParts(new Date(utc));
    const wanted = Date.UTC(year, month - 1, day, hour, minute);
    const actual = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
    if (wanted === actual) return new Date(utc);
    utc += wanted - actual;
  }
  return new Date(utc);
}

/** Due at 4:30 p.m. Toronto on the nth Ontario business day after `iso`. */
export function addOntarioBusinessDays(iso: string, days: number): string {
  const start = zonedParts(new Date(iso));
  let cursor = { year: start.year, month: start.month, day: start.day };
  let left = days;
  for (let i = 0; i < 21 && left > 0; i += 1) {
    cursor = addCalendarDays(cursor.year, cursor.month, cursor.day, 1);
    if (isBusinessYmd(cursor.year, cursor.month, cursor.day)) left -= 1;
  }
  return torontoTime(cursor.year, cursor.month, cursor.day, 16, 30).toISOString();
}

/** Elapsed time in minutes, hours, or days. Never seconds. */
export function formatElapsed(from: Date, to: Date): string {
  const ms = Math.max(0, to.getTime() - from.getTime());
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return "Less than a minute";
  if (minutes < 60) return minutes === 1 ? "1 minute" : `${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  if (hours < 48) {
    const hourLabel = hours === 1 ? "1 hour" : `${hours} hours`;
    if (rem === 0) return hourLabel;
    const minLabel = rem === 1 ? "1 minute" : `${rem} minutes`;
    return `${hourLabel} ${minLabel}`;
  }
  const days = Math.floor(hours / 24);
  return days === 1 ? "1 day" : `${days} days`;
}

export function formatWhen(iso?: string, locale = "en-CA"): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(locale, {
    timeZone: TORONTO,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(date);
}

export function toDateTimeLocal(iso?: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes()
  )}`;
}

export function fromDateTimeLocal(value: string): string {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
}

export function isLateEntry(eventIso: string | undefined, entryAt: Date, thresholdMinutes = 30): boolean {
  if (!eventIso) return false;
  const eventAt = new Date(eventIso);
  if (Number.isNaN(eventAt.getTime())) return false;
  return entryAt.getTime() - eventAt.getTime() > thresholdMinutes * 60000;
}
